<template>
  <b-card no-body class="account-balance-card">
    <b-card-header class="account-balance-card__header">
      <div
        class="d-flex justify-content-between align-items-start align-items-md-center flex-wrap w-100"
      >
        <div class="d-flex align-items-center mb-1 mb-md-0">
          <div class="account-balance-card__icon-wrap mr-1">
            <span class="account-balance-card__rs">Rs</span>
          </div>
          <div>
            <h4 class="mb-0">{{ $t("Cash & Bank Balance") }}</h4>
            <small class="text-muted">{{
              $t("All cash and bank account balances for selected period")
            }}</small>
          </div>
        </div>

        <div class="account-balance-card__toolbar">
          <div class="date-filter">
            <div class="date-filter__control">
              <span class="date-filter__icon" aria-hidden="true">
                <feather-icon icon="CalendarIcon" size="16" />
              </span>
              <div
                class="date-filter__field"
                role="button"
                tabindex="0"
                @click="openDatePicker"
                @keydown.enter.prevent="openDatePicker"
              >
                <span class="date-filter__label">{{ filterDateLabel }}</span>
                <flat-pickr
                  ref="datePicker"
                  v-model="dateRange"
                  :config="rangeConfig"
                  class="date-filter__input"
                  :placeholder="$t('Select date range')"
                  @on-close="onDateClose"
                />
              </div>
              <span class="date-filter__divider" aria-hidden="true" />
              <button
                type="button"
                class="date-filter__refresh"
                v-b-tooltip.hover.top
                :title="$t('Refresh')"
                :disabled="loading"
                @click="loadData"
              >
                <b-spinner v-if="loading" small variant="primary" />
                <feather-icon v-else icon="RefreshCwIcon" size="15" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </b-card-header>

    <b-card-body class="account-balance-card__body">
      <div v-if="loading && !hasLoadedOnce" class="account-balance-skeleton">
        <b-row class="match-height mb-2">
          <b-col xl="4" md="4" sm="12" class="mb-1 mb-md-0" v-for="n in 3" :key="'sk-tile-' + n">
            <div class="summary-tile summary-tile--skeleton">
              <b-skeleton width="45%" height="12px" animation="wave" class="mb-75" />
              <b-skeleton width="75%" height="28px" animation="wave" class="mb-50" />
              <b-skeleton width="35%" height="10px" animation="wave" />
            </div>
          </b-col>
        </b-row>

        <b-row class="match-height">
          <b-col lg="6" cols="12" class="mb-1 mb-lg-0" v-for="n in 2" :key="'sk-panel-' + n">
            <div class="account-panel account-panel--skeleton">
              <div class="account-panel__head account-panel__head--skeleton">
                <div class="w-100">
                  <b-skeleton width="40%" height="18px" animation="wave" class="mb-50" />
                  <b-skeleton width="55%" height="14px" animation="wave" />
                </div>
              </div>
              <div class="account-panel__list p-1">
                <div
                  v-for="row in 3"
                  :key="'sk-row-' + n + '-' + row"
                  class="account-panel__row account-panel__row--skeleton"
                >
                  <div class="flex-grow-1">
                    <b-skeleton width="60%" height="14px" animation="wave" class="mb-50" />
                    <b-skeleton width="35%" height="10px" animation="wave" />
                  </div>
                  <b-skeleton width="90px" height="16px" animation="wave" />
                </div>
              </div>
            </div>
          </b-col>
        </b-row>
      </div>

      <div
        v-else-if="loadError"
        class="text-center py-4 text-muted"
      >
        <feather-icon icon="AlertCircleIcon" size="36" class="mb-50 text-warning" />
        <p class="mb-50">{{ $t("Unable to load account balances.") }}</p>
        <b-button size="sm" variant="outline-primary" @click="loadData">
          {{ $t("Try again") }}
        </b-button>
      </div>

      <div
        v-else-if="!sections.hasCash && !sections.hasBank"
        class="text-center py-4 text-muted"
      >
        <feather-icon icon="InboxIcon" size="36" class="mb-50" />
        <p class="mb-0">{{ $t("No cash or bank accounts found.") }}</p>
      </div>

      <template v-else>
        <div v-if="loading" class="account-balance-card__refreshing" aria-hidden="true" />
        <b-row class="match-height mb-2" :class="{ 'is-dimmed': loading && hasLoadedOnce }">
          <b-col xl="4" md="4" sm="12" class="mb-1 mb-md-0">
            <div class="summary-tile summary-tile--cash">
              <span class="summary-tile__label">{{ $t("Total Cash") }}</span>
              <span
                class="summary-tile__value"
                :class="amountTone(sections.cashTotal)"
              >
                {{ formatAmount(sections.cashTotal) }}
              </span>
              <small class="summary-tile__meta">
                {{ sections.cashAccounts.length }} {{ $t("account(s)") }}
              </small>
            </div>
          </b-col>
          <b-col xl="4" md="4" sm="12" class="mb-1 mb-md-0">
            <div class="summary-tile summary-tile--bank">
              <span class="summary-tile__label">{{ $t("Total Bank") }}</span>
              <span
                class="summary-tile__value"
                :class="amountTone(sections.bankTotal)"
              >
                {{ formatAmount(sections.bankTotal) }}
              </span>
              <small class="summary-tile__meta">
                {{ sections.bankAccounts.length }} {{ $t("account(s)") }}
              </small>
            </div>
          </b-col>
          <b-col xl="4" md="4" sm="12">
            <div class="summary-tile summary-tile--net">
              <span class="summary-tile__label">{{ $t("Net Cash & Bank") }}</span>
              <span
                class="summary-tile__value"
                :class="amountTone(sections.netTotal)"
              >
                {{ formatAmount(sections.netTotal) }}
              </span>
              <small class="summary-tile__meta">{{ periodLabel }}</small>
            </div>
          </b-col>
        </b-row>

        <b-row class="match-height" :class="{ 'is-dimmed': loading && hasLoadedOnce }">
          <b-col lg="6" cols="12" class="mb-1 mb-lg-0" v-if="sections.hasCash">
            <div class="account-panel account-panel--cash">
              <div class="account-panel__head">
                <div>
                  <h5 class="mb-0">{{ $t("Cash Accounts") }}</h5>
                  <small>{{ formatAmount(sections.cashTotal) }}</small>
                </div>
                <feather-icon icon="BriefcaseIcon" size="20" />
              </div>

              <div
                v-for="group in sections.cashGroups"
                :key="'cash-group-' + group.id"
                class="account-panel__group"
              >
                <button
                  v-if="group.accounts.length > 1 || group.title !== 'Accounts'"
                  type="button"
                  class="account-panel__group-head"
                  @click="toggleGroup('cash', group.id)"
                >
                  <feather-icon
                    :icon="
                      isGroupOpen('cash', group.id)
                        ? 'ChevronDownIcon'
                        : 'ChevronRightIcon'
                    "
                    size="16"
                  />
                  <span class="account-panel__group-title">{{ group.title }}</span>
                  <span v-if="group.code" class="account-panel__group-code">{{
                    group.code
                  }}</span>
                  <span
                    class="account-panel__group-total"
                    :class="amountTone(group.totalBalance)"
                  >
                    {{ formatAmount(group.totalBalance) }}
                  </span>
                </button>

                <div
                  v-show="
                    group.accounts.length === 1 &&
                    group.title === 'Accounts'
                      ? true
                      : isGroupOpen('cash', group.id)
                  "
                  class="account-panel__list"
                >
                  <div
                    v-for="acc in group.accounts"
                    :key="'cash-' + acc.id"
                    class="account-panel__row"
                  >
                    <div class="account-panel__row-main">
                      <span class="account-panel__row-title">{{ acc.title }}</span>
                      <span class="account-panel__row-code">{{ acc.code }}</span>
                    </div>
                    <span
                      class="account-panel__row-amount"
                      :class="amountTone(acc.displayBalance)"
                    >
                      {{ formatAmount(acc.displayBalance) }}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </b-col>

          <b-col lg="6" cols="12" v-if="sections.hasBank">
            <div class="account-panel account-panel--bank">
              <div class="account-panel__head">
                <div>
                  <h5 class="mb-0">{{ $t("Bank Accounts") }}</h5>
                  <small>{{ formatAmount(sections.bankTotal) }}</small>
                </div>
                <feather-icon icon="CreditCardIcon" size="20" />
              </div>

              <div
                v-for="group in sections.bankGroups"
                :key="'bank-group-' + group.id"
                class="account-panel__group"
              >
                <button
                  v-if="group.accounts.length > 1 || group.title !== 'Accounts'"
                  type="button"
                  class="account-panel__group-head"
                  @click="toggleGroup('bank', group.id)"
                >
                  <feather-icon
                    :icon="
                      isGroupOpen('bank', group.id)
                        ? 'ChevronDownIcon'
                        : 'ChevronRightIcon'
                    "
                    size="16"
                  />
                  <span class="account-panel__group-title">{{ group.title }}</span>
                  <span v-if="group.code" class="account-panel__group-code">{{
                    group.code
                  }}</span>
                  <span
                    class="account-panel__group-total"
                    :class="amountTone(group.totalBalance)"
                  >
                    {{ formatAmount(group.totalBalance) }}
                  </span>
                </button>

                <div
                  v-show="
                    group.accounts.length === 1 &&
                    group.title === 'Accounts'
                      ? true
                      : isGroupOpen('bank', group.id)
                  "
                  class="account-panel__list"
                >
                  <div
                    v-for="acc in group.accounts"
                    :key="'bank-' + acc.id"
                    class="account-panel__row"
                  >
                    <div class="account-panel__row-main">
                      <span class="account-panel__row-title">{{ acc.title }}</span>
                      <span class="account-panel__row-code">{{ acc.code }}</span>
                    </div>
                    <span
                      class="account-panel__row-amount"
                      :class="amountTone(acc.displayBalance)"
                    >
                      {{ formatAmount(acc.displayBalance) }}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </b-col>
        </b-row>
      </template>
    </b-card-body>
  </b-card>
</template>

<script>
import {
  BCard,
  BCardHeader,
  BCardBody,
  BRow,
  BCol,
  BButton,
  BSpinner,
  BSkeleton,
  VBTooltip,
} from "bootstrap-vue";
import flatPickr from "vue-flatpickr-component";
import { mapActions } from "vuex";
import {
  buildCashBankSections,
  getCurrentMonthRange,
  formatIsoDate,
  parseIsoDateLocal,
  toNumber,
} from "./accountBalanceTree";

export default {
  name: "AccountBalanceCard",
  components: {
    BCard,
    BCardHeader,
    BCardBody,
    BRow,
    BCol,
    BButton,
    BSpinner,
    BSkeleton,
    flatPickr,
  },
  directives: {
    "b-tooltip": VBTooltip,
  },
  data() {
    const { dtFrom, dtTo } = getCurrentMonthRange();
    return {
      loading: true,
      hasLoadedOnce: false,
      loadError: false,
      loadSeq: 0,
      suppressDateEvents: true,
      accounts: [],
      sections: {
        cashGroups: [],
        bankGroups: [],
        cashAccounts: [],
        bankAccounts: [],
        cashTotal: 0,
        bankTotal: 0,
        netTotal: 0,
        hasCash: false,
        hasBank: false,
      },
      openGroups: {},
      dateRange: `${dtFrom} to ${dtTo}`,
      dtFrom,
      dtTo,
      rangeConfig: {
        mode: "range",
        dateFormat: "Y-m-d",
        defaultDate: [parseIsoDateLocal(dtFrom), parseIsoDateLocal(dtTo)],
        allowInput: false,
        disableMobile: true,
        locale: { rangeSeparator: " to " },
      },
      dateChangeTimer: null,
    };
  },
  computed: {
    periodLabel() {
      if (!this.dtFrom || !this.dtTo) return this.$t("All dates");
      return `${this.formatDisplayDate(this.dtFrom)} – ${this.formatDisplayDate(
        this.dtTo
      )}`;
    },
    filterDateLabel() {
      if (!this.dtFrom || !this.dtTo) return this.$t("Select date range");
      return `${this.formatDisplayDate(this.dtFrom)} to ${this.formatDisplayDate(
        this.dtTo
      )}`;
    },
  },
  mounted() {
    this.loadData();
    this.$nextTick(() => {
      this.suppressDateEvents = false;
    });
  },
  beforeDestroy() {
    if (this.dateChangeTimer) clearTimeout(this.dateChangeTimer);
  },
  methods: {
    ...mapActions({ get: "get" }),

    openDatePicker() {
      const picker = this.$refs.datePicker;
      if (picker && picker.fp) {
        const anchor = this.$el.querySelector(".date-filter__field");
        if (anchor) {
          picker.fp.config.positionElement = anchor;
        }
        picker.fp.open();
      }
    },

    groupKey(type, id) {
      return `${type}-${id}`;
    },

    isGroupOpen(type, id) {
      return !!this.openGroups[this.groupKey(type, id)];
    },

    toggleGroup(type, id) {
      const key = this.groupKey(type, id);
      this.$set(this.openGroups, key, !this.openGroups[key]);
    },

    openAllGroups() {
      const next = {};
      this.sections.cashGroups.forEach((g) => {
        next[this.groupKey("cash", g.id)] = true;
      });
      this.sections.bankGroups.forEach((g) => {
        next[this.groupKey("bank", g.id)] = true;
      });
      this.openGroups = next;
    },

    rebuildSections() {
      this.sections = buildCashBankSections(this.accounts);
      this.openAllGroups();
    },

    onDateClose(selectedDates) {
      if (this.suppressDateEvents) return;
      if (!selectedDates || selectedDates.length < 2) return;
      this.applyDateRange(selectedDates[0], selectedDates[1]);
    },

    applyDateRange(fromDate, toDate) {
      const nextFrom = formatIsoDate(fromDate);
      const nextTo = formatIsoDate(toDate);
      if (nextFrom === this.dtFrom && nextTo === this.dtTo) return;

      this.dtFrom = nextFrom;
      this.dtTo = nextTo;
      this.dateRange = `${nextFrom} to ${nextTo}`;

      if (this.dateChangeTimer) clearTimeout(this.dateChangeTimer);
      this.dateChangeTimer = setTimeout(() => {
        this.loadData();
      }, 250);
    },

    formatDisplayDate(iso) {
      const dt = parseIsoDateLocal(iso);
      if (!dt || Number.isNaN(dt.getTime())) return iso;
      const months = [
        "Jan", "Feb", "Mar", "Apr", "May", "Jun",
        "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
      ];
      return `${dt.getDate()} ${months[dt.getMonth()]}, ${dt.getFullYear()}`;
    },

    amountTone(val) {
      return toNumber(val) < 0 ? "amount--negative" : "amount--positive";
    },

    formatAmount(val) {
      const safe = toNumber(val);
      return `Rs. ${safe.toLocaleString(undefined, {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      })}`;
    },

    normalizeAccounts(res) {
      if (Array.isArray(res)) return res;
      if (res && Array.isArray(res.data)) return res.data;
      if (res && res.data && Array.isArray(res.data.records)) return res.data.records;
      return [];
    },

    async loadData() {
      const seq = ++this.loadSeq;
      this.loading = true;
      this.loadError = false;

      try {
        const userData = this.$store.state.userData || {};
        if (!userData.db || !userData.token) {
          this.loadError = true;
          return;
        }

        let url =
          this.$store.state.domain +
          "Accounts/AccountBalance?db=" +
          userData.db +
          "&cID=" +
          userData.cId +
          "&cashFlowCategory=cash_account,bank_account";

        if (this.dtFrom) url += "&dtFrom=" + encodeURIComponent(this.dtFrom);
        if (this.dtTo) url += "&dtTo=" + encodeURIComponent(this.dtTo);

        const res = await this.get({
          url,
          token: userData.token,
          returnData: true,
        });

        if (seq !== this.loadSeq) return;

        if (res === false) {
          this.loadError = true;
          this.accounts = [];
          this.rebuildSections();
          return;
        }

        this.accounts = this.normalizeAccounts(res);
        this.rebuildSections();
        this.hasLoadedOnce = true;
      } catch (e) {
        if (seq !== this.loadSeq) return;
        console.error("AccountBalance load failed:", e);
        this.loadError = true;
        this.accounts = [];
        this.rebuildSections();
      } finally {
        if (seq === this.loadSeq) {
          this.loading = false;
        }
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.account-balance-card {
  border: 1px solid #ebe9f1;
  box-shadow: 0 4px 24px rgba(34, 41, 47, 0.06);

  &__header {
    padding: 1.1rem 1.35rem;
    background: linear-gradient(135deg, #f8fffe 0%, #f6f9ff 100%);
    border-bottom: 1px solid #ebe9f1;
  }

  &__icon-wrap {
    width: 44px;
    height: 44px;
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: linear-gradient(135deg, #28c76f 0%, #20a960 100%);
    color: #fff;
    flex-shrink: 0;
  }

  &__rs {
    font-size: 0.95rem;
    font-weight: 800;
    letter-spacing: 0.02em;
    line-height: 1;
  }

  &__toolbar {
    display: flex;
    align-items: center;
    gap: 0.65rem;
    flex-shrink: 0;
    margin-left: auto;
  }
  &__body {
    padding: 1.15rem 1.35rem 1.35rem;
    position: relative;
  }

  &__refreshing {
    position: absolute;
    inset: 0;
    background: rgba(255, 255, 255, 0.45);
    z-index: 2;
    pointer-events: none;
  }
}

.date-filter {
  &__control {
    display: inline-flex;
    align-items: stretch;
    min-width: 320px;
    max-width: 100%;
    height: 40px;
    background: #fff;
    border: 1px solid #e4e4e8;
    border-radius: 10px;
    box-shadow: 0 1px 4px rgba(34, 41, 47, 0.05);
    overflow: hidden;
    transition: border-color 0.15s ease, box-shadow 0.15s ease;

    &:hover {
      border-color: #d8d8de;
    }

    &:focus-within {
      border-color: #7367f0;
      box-shadow: 0 0 0 3px rgba(115, 103, 240, 0.14);
    }
  }

  &__icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    flex-shrink: 0;
    color: #7367f0;
    background: rgba(115, 103, 240, 0.08);
    border-right: 1px solid #efeff2;
  }

  &__field {
    position: relative;
    flex: 1;
    min-width: 0;
    display: flex;
    align-items: center;
    padding: 0 0.85rem;
    cursor: pointer;
    user-select: none;
  }

  &__label {
    font-size: 0.875rem;
    font-weight: 500;
    color: #5e5873;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    line-height: 1.3;
  }

  &__divider {
    width: 1px;
    align-self: stretch;
    background: #efeff2;
    flex-shrink: 0;
  }

  &__refresh {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    flex-shrink: 0;
    padding: 0;
    border: none;
    background: #fff;
    color: #7367f0;
    cursor: pointer;
    transition: background 0.15s ease, color 0.15s ease;

    &:hover:not(:disabled) {
      background: rgba(115, 103, 240, 0.08);
      color: #6258cc;
    }

    &:disabled {
      cursor: not-allowed;
      opacity: 0.55;
    }
  }
}

::v-deep .date-filter__input,
::v-deep .date-filter__input.form-control {
  position: absolute !important;
  width: 1px !important;
  height: 1px !important;
  padding: 0 !important;
  margin: -1px !important;
  overflow: hidden !important;
  clip: rect(0, 0, 0, 0) !important;
  white-space: nowrap !important;
  border: 0 !important;
  box-shadow: none !important;
  outline: none !important;
  opacity: 0 !important;
  pointer-events: none !important;
}

.is-dimmed {
  opacity: 0.55;
  pointer-events: none;
  transition: opacity 0.15s ease;
}

@media (max-width: 575.98px) {
  .date-filter__control {
    min-width: 100%;
    width: 100%;
  }

  .account-balance-card__toolbar {
    width: 100%;
    margin-left: 0 !important;
    margin-top: 0.75rem;
  }
}

.account-balance-skeleton {
  animation: fadeIn 0.2s ease;
}

.summary-tile--skeleton {
  background: #fff;
  border-color: #ebe9f1;
}

.account-panel--skeleton {
  border-color: #ebe9f1;
}

.account-panel__head--skeleton {
  background: #f3f2f7 !important;
  min-height: 72px;
}

.account-panel__row--skeleton {
  border-top: 1px solid #f3f2f7;
}

@keyframes fadeIn {
  from {
    opacity: 0.6;
  }
  to {
    opacity: 1;
  }
}

.summary-tile {
  border-radius: 0.75rem;
  padding: 1rem 1.1rem;
  height: 100%;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  border: 1px solid transparent;

  &--cash {
    background: linear-gradient(135deg, #eefbf3 0%, #f8fffb 100%);
    border-color: #b9f0cf;
  }

  &--bank {
    background: linear-gradient(135deg, #eef5ff 0%, #f7faff 100%);
    border-color: #b8d4ff;
  }

  &--net {
    background: linear-gradient(135deg, #f5f3ff 0%, #faf9ff 100%);
    border-color: #d4cbff;
  }

  &__label {
    font-size: 0.78rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: #6e6b7b;
  }

  &__value {
    font-size: 1.35rem;
    font-weight: 800;
    line-height: 1.2;
  }

  &__meta {
    color: #8a8594;
    font-size: 0.78rem;
  }
}

.account-panel {
  border: 1px solid #ebe9f1;
  border-radius: 0.75rem;
  overflow: hidden;
  height: 100%;
  background: #fff;

  &--cash .account-panel__head {
    background: linear-gradient(135deg, #28c76f 0%, #20a960 100%);
  }

  &--bank .account-panel__head {
    background: linear-gradient(135deg, #7367f0 0%, #5e50ee 100%);
  }

  &__head {
    color: #fff;
    padding: 0.95rem 1.1rem;
    display: flex;
    justify-content: space-between;
    align-items: center;

    h5 {
      color: #fff;
      font-weight: 700;
    }

    small {
      color: rgba(255, 255, 255, 0.92);
      font-weight: 600;
    }
  }

  &__group + &__group {
    border-top: 1px solid #f1f0f5;
  }

  &__group-head {
    width: 100%;
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.8rem 1rem;
    border: none;
    background: #fafafa;
    text-align: left;
    cursor: pointer;

    &:hover {
      background: #f3f2f7;
    }
  }

  &__group-title {
    font-weight: 700;
    color: #4b465c;
    flex: 1;
  }

  &__group-code {
    font-size: 0.75rem;
    color: #8a8594;
    font-family: monospace;
  }

  &__group-total {
    font-weight: 700;
    font-size: 0.92rem;
    white-space: nowrap;
  }

  &__list {
    background: #fff;
  }

  &__row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
    padding: 0.85rem 1rem 0.85rem 1.35rem;
    border-top: 1px solid #f3f2f7;

    &:hover {
      background: #fcfcfd;
    }
  }

  &__row-main {
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 0.15rem;
  }

  &__row-title {
    font-weight: 600;
    color: #4b465c;
    word-break: break-word;
  }

  &__row-code {
    font-size: 0.76rem;
    color: #8a8594;
    font-family: monospace;
  }

  &__row-amount {
    font-weight: 800;
    font-size: 0.98rem;
    white-space: nowrap;
  }
}

.amount--positive {
  color: #28c76f;
}

.amount--negative {
  color: #ea5455;
}
</style>
